import { Truck } from 'lucide-react';
import { lazy } from 'react';
import { msg } from '@/lib/i18n';
import { registerInvalidation } from '@/lib/realtime';
import { registerEntityTab } from '../entityTabs';
import { defineModule } from '../types';
import { fleetKeys } from './api';
import { placeholderTab } from './components/PlaceholderTab';
import {
  deviceRoute,
  devicesAliasRoute,
  devicesRoute,
  driverRoute,
  driversRoute,
  fleetIndexRoute,
  groupRoute,
  groupsRoute,
  vehicleRoute,
  vehiclesRoute,
} from './routes';

/** Fleet: vehicles, drivers, devices and groups — the reference module for the web app. */
export const fleetModule = defineModule({
  key: 'fleet',
  routes: [fleetIndexRoute, vehiclesRoute, vehicleRoute, driversRoute, driverRoute, devicesRoute, devicesAliasRoute, deviceRoute, groupsRoute, groupRoute],
  nav: [
    {
      key: 'fleet',
      aliases: ['devices'],
      label: msg('Fleet'),
      description: msg('Vehicles, drivers, devices and groups'),
      icon: Truck,
      to: '/fleet/vehicles',
      section: 'manage',
      order: 80,
      module: 'fleet',
      permission: ['fleet.vehicle.read', 'fleet.driver.read', 'fleet.device.read', 'core.group.read'],
      match: ['/fleet', '/vehicles', '/drivers', '/devices'],
      children: [
        { key: 'vehicles', label: msg('Vehicles'), to: '/fleet/vehicles', permission: 'fleet.vehicle.read', match: ['/fleet/vehicles', '/vehicles'] },
        { key: 'drivers', label: msg('Drivers'), to: '/fleet/drivers', permission: 'fleet.driver.read', match: ['/fleet/drivers', '/drivers'] },
        { key: 'devices', label: msg('Devices'), to: '/fleet/devices', permission: 'fleet.device.read', match: ['/fleet/devices', '/devices'] },
        { key: 'groups', label: msg('Groups'), to: '/fleet/groups', permission: 'core.group.read' },
      ],
    },
  ],
  setup() {
    // Entity page tabs (IA §3). Other modules add theirs the same way.
    registerEntityTab('vehicle', { key: 'overview', label: msg('Overview'), order: 10, component: lazy(() => import('./vehicles/tabs/VehicleOverviewTab')) });
    registerEntityTab('vehicle', {
      key: 'activity',
      label: msg('Activity'),
      order: 20,
      component: placeholderTab(
        msg('Activity'),
        msg('The continuous timeline of this vehicle — driving, stops, events and video — arrives with the telemetry and trips modules.'),
      ),
    });
    registerEntityTab('vehicle', {
      key: 'performance',
      label: msg('Performance'),
      order: 30,
      component: placeholderTab(msg('Performance'), msg('Scores and trends for this vehicle arrive with the insights module.')),
    });
    registerEntityTab('vehicle', { key: 'details', label: msg('Details'), order: 40, component: lazy(() => import('./vehicles/tabs/VehicleDetailsTab')) });
    registerEntityTab('vehicle', {
      key: 'devices',
      label: msg('Devices'),
      order: 50,
      permission: 'fleet.device.read',
      component: lazy(() => import('./vehicles/tabs/VehicleDevicesTab')),
    });

    registerEntityTab('driver', { key: 'overview', label: msg('Overview'), order: 10, component: lazy(() => import('./drivers/tabs/DriverOverviewTab')) });
    registerEntityTab('driver', {
      key: 'activity',
      label: msg('Activity'),
      order: 20,
      component: placeholderTab(
        msg('Activity'),
        msg("This driver's timeline — trips, events and driving hours — arrives with the telemetry and trips modules."),
      ),
    });
    registerEntityTab('driver', {
      key: 'performance',
      label: msg('Performance'),
      order: 30,
      component: placeholderTab(msg('Performance'), msg('Scores, trends and coaching history for this driver arrive with the insights and coaching modules.')),
    });
    registerEntityTab('driver', { key: 'details', label: msg('Details'), order: 40, component: lazy(() => import('./drivers/tabs/DriverDetailsTab')) });

    registerEntityTab('device', { key: 'overview', label: msg('Overview'), order: 10, component: lazy(() => import('./devices/tabs/DeviceOverviewTab')) });
    registerEntityTab('device', {
      key: 'details',
      label: msg('Details'),
      order: 20,
      permission: 'fleet.device.manage',
      component: lazy(() => import('./devices/tabs/DeviceDetailsTab')),
    });
    // The raw console (IA §3 device › Messages): what the device sent, as sent and as mapped.
    registerEntityTab('device', {
      key: 'messages',
      label: msg('Messages'),
      order: 35,
      permission: 'telemetry.raw.read',
      module: 'telemetry',
      component: lazy(() => import('./devices/tabs/DeviceMessagesTab')),
    });
    registerEntityTab('device', {
      key: 'health',
      label: msg('Health'),
      order: 30,
      component: placeholderTab(msg('Health'), msg('Message rates, gaps, signal and installation checks for this device arrive with the devices module.')),
    });

    // Realtime: an event about a subject makes these queries stale.
    registerInvalidation('vehicle', (n) => [fleetKeys.vehicle(n.subject_id ?? ''), ['fleet', 'vehicles']]);
    registerInvalidation('driver', (n) => [fleetKeys.driver(n.subject_id ?? ''), ['fleet', 'drivers']]);
    registerInvalidation('device', (n) => [fleetKeys.device(n.subject_id ?? ''), ['fleet', 'devices'], ['fleet', 'vehicles'], ['fleet', 'vehicle']]);
    registerInvalidation('group', () => [fleetKeys.groupings, ['fleet', 'members'], ['fleet', 'vehicles'], ['fleet', 'drivers']]);
  },
});
